import ChevronDoubleLeftIcon from "@assets/icons/chevron-double-left.svg";
import ChevronDoubleRightIcon from "@assets/icons/chevron-double-right.svg";
import { libraryFilterManager } from "@core/service-container";
import styled from "@emotion/styled";
import { Tooltip } from "@ui/components/common/tooltip";
import { colors } from "@ui/style/colors";
import { row, stack } from "@ui/style/layout";
import { animate, motion, useMotionTemplate, useMotionValue, useTransform } from "framer-motion";
import { useObservable } from "micro-observables";
import React, { useEffect, useState } from "react";

interface LibrarySidebarProps {
	pushedContent: React.ReactNode;
	title: string;
	disabled?: boolean;
	disabledTooltip?: string;
	children: React.ReactNode;
}

export const LibrarySidebar: React.FC<LibrarySidebarProps> = ({
	children,
	pushedContent,
	title,
	disabled,
	disabledTooltip,
}) => {
	const sidebarVisible = useObservable(libraryFilterManager.sidebarVisible);
	const [sidebarTop, setSidebarTop] = useState<number>(0);
	const sidebarId = "sidebar";
	const progressValue = useMotionValue(disabled ? 0 : 1);

	const slideX = useTransform(progressValue, [0, 1], [0, SIDEBAR_WIDTH]);
	const width = useMotionTemplate`${slideX}px`;
	const x = useTransform(progressValue, [0, 1], [OPENING_BUTTON_WIDTH, 0]);
	const pushedWidth = useMotionTemplate`calc(100% - ${slideX}px)`;

	useEffect(() => {
		const controls = animate(progressValue, sidebarVisible ? 1 : 0, {
			ease: "easeInOut",
		});

		return controls.stop;
	}, [progressValue, sidebarVisible]);

	useEffect(() => {
		const sidebarRect = document.getElementById(sidebarId)?.getBoundingClientRect();
		if (sidebarRect) {
			setSidebarTop(sidebarRect.top + document.documentElement.scrollTop);
		}
	}, []);

	useEffect(() => {
		libraryFilterManager.setSidebarVisible(!disabled);
	}, [disabled]);

	return (
		<Container>
			<Sidebar id={sidebarId} style={{ width }}>
				<SidebarContainer style={{ maxHeight: `calc(100vh - ${sidebarTop}px)` }}>
					<HeaderContainer>
						<h3>{title}</h3>
						<ClosingButton onClick={() => libraryFilterManager.setSidebarVisible(false)}>
							<ChevronDoubleLeftIcon width={24} height={24} color={colors.grey} fill={colors.white} />
						</ClosingButton>
						<Tooltip content={disabledTooltip} disabled={!disabled} placement="right">
							<OpeningButtonWrapper style={{ x }}>
								<OpeningButton
									className="p-button"
									disabled={disabled}
									onClick={() => !disabled && libraryFilterManager.setSidebarVisible(true)}
								>
									<ChevronDoubleRightIcon
										width={24}
										height={24}
										color={disabled ? colors.grey : colors.white}
										fill={disabled ? colors.grey25 : colors.primary}
									/>
								</OpeningButton>
							</OpeningButtonWrapper>
						</Tooltip>
					</HeaderContainer>
					{children}
				</SidebarContainer>
			</Sidebar>
			<PushedContainer style={{ width: pushedWidth }}>{pushedContent}</PushedContainer>
		</Container>
	);
};

const Container = styled.div`
	${row(undefined, undefined, "stretch")};
	flex: 1;
`;

const SIDEBAR_WIDTH = 450;
const Sidebar = styled(motion.div)`
	background-color: ${colors.white};
	z-index: 20;
	${stack(undefined, "flexstart", "flexend")};
`;

const SidebarContainer = styled.div`
	${stack(undefined, "flexstart", "stretch")};
	width: ${SIDEBAR_WIDTH}px;
	flex: 1;
	border-right: 1px solid var(--surface-border);
`;

const PushedContainer = styled(motion.div)`
	${row(undefined, undefined, "stretch")};
	flex: 1;
`;

const HeaderContainer = styled.div`
	position: relative;
	${row(undefined, "spacebetween", "center")};
	background-color: ${colors.white};

	h3 {
		padding: 20px 0px 6px 20px;
		font-size: 20px;
	}
`;

const OPENING_BUTTON_WIDTH = 76;
const OpeningButton = styled.button`
	border-radius: 0px 24px 24px 0px;
	padding: 0 12px 0 16px;
	height: 38px;

	${row(5, "flexend", "center")};
`;

const OpeningButtonWrapper = styled(motion.div)`
	position: absolute;
	right: 0;
	top: calc(50% - 16px);
	z-index: -1;
`;

const ClosingButton = styled.button`
	${row(5, "flexend", "center")};
	color: ${colors.grey};
	height: 38px;
	padding: 0 16px 0 12px;
	border: none;
	border-radius: 24px 0px 0px 24px;
	background: ${colors.transparent};
	cursor: pointer;

	:hover {
		background: ${colors.primary10};
	}
`;
